/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React from 'react';
import { WindowManagerState, WindowConstraints, WindowPosition, WindowSize, DragOffset, WindowState } from 'interfaces/windowSize.interface';
import { VIDEOCHAT_WINDOW_DEFAULTS } from 'constants/windowSize.const';

/**
 * Calculates the default position for minimized window (top-right corner).
 * @returns Window position object with x and y coordinates
 */
export const getDefaultMinimizedPosition = (): WindowPosition => ({
  x: window.innerWidth - 320,
  y: 20
});

/**
 * Creates initial state object for window manager with default or provided dimensions.
 * @param windowSize - Optional initial window size; defaults to VIDEOCHAT_WINDOW_DEFAULTS.SIZE
 * @param position - Optional initial position; defaults to VIDEOCHAT_WINDOW_DEFAULTS.POSITION
 * @returns Complete window manager state with all properties initialized
 */
export const createInitialWindowState = (
  windowSize: WindowSize = VIDEOCHAT_WINDOW_DEFAULTS.SIZE,
  position: WindowPosition = VIDEOCHAT_WINDOW_DEFAULTS.POSITION
): WindowManagerState => ({
  windowSize,
  position,
  minimizedPosition: getDefaultMinimizedPosition(),
  isMaximized: false,
  isMinimized: false,
  isDragging: false,
  isDraggingMinimized: false,
  isResizing: false,
  resizeDirection: '',
  dragOffset: { x: 0, y: 0 },
  minimizedDragOffset: { x: 0, y: 0 },
  previousWindowState: { size: windowSize, position }
});

/**
 * Ensures window position stays within viewport boundaries.
 * @param newPosition - Proposed window position
 * @param windowSize - Current window dimensions
 * @param isMinimized - Whether window is in minimized state
 * @returns Adjusted position that keeps window within screen bounds
 */
export const calculateBoundedPosition = (
  newPosition: WindowPosition,
  windowSize: WindowSize,
  isMinimized: boolean = false
): WindowPosition => {
  const effectiveHeight = isMinimized ? 60 : windowSize.height;
  const effectiveWidth = isMinimized ? 300 : windowSize.width;
  
  const maxX = window.innerWidth - effectiveWidth;
  const maxY = window.innerHeight - effectiveHeight;
  
  return {
    x: Math.max(0, Math.min(newPosition.x, maxX)),
    y: Math.max(0, Math.min(newPosition.y, maxY))
  };
};

/**
 * Calculates new window position during drag operation accounting for drag offset.
 * @param clientX - Current mouse X coordinate
 * @param clientY - Current mouse Y coordinate
 * @param dragOffset - Offset from window corner to mouse position when drag started
 * @param windowSize - Current window dimensions
 * @param isMinimized - Whether window is minimized
 * @returns New bounded position for the window
 */
export const calculateDragPosition = (
  clientX: number,
  clientY: number,
  dragOffset: DragOffset,
  windowSize: WindowSize,
  isMinimized: boolean = false
): WindowPosition => {
  const newPosition = {
    x: clientX - dragOffset.x,
    y: clientY - dragOffset.y
  };
  
  return calculateBoundedPosition(newPosition, windowSize, isMinimized);
};

/**
 * Calculates new window dimensions and position during resize operation.
 * @param clientX - Current mouse X coordinate
 * @param clientY - Current mouse Y coordinate
 * @param currentRect - Current window bounding rectangle
 * @param currentSize - Current window dimensions
 * @param currentPosition - Current window position
 * @param resizeDirection - Direction of resize (e.g., 'right', 'bottom-left')
 * @param constraints - Min/max width and height constraints
 * @returns New window size and position respecting constraints
 */
export const calculateResizedDimensions = (
  clientX: number,
  clientY: number,
  currentRect: DOMRect,
  currentSize: WindowSize,
  currentPosition: WindowPosition,
  resizeDirection: string,
  constraints: WindowConstraints = VIDEOCHAT_WINDOW_DEFAULTS.CONSTRAINTS
) => {
  let newWidth = currentSize.width;
  let newHeight = currentSize.height;
  let newX = currentPosition.x;
  let newY = currentPosition.y;

  if (resizeDirection.includes('right')) {
    newWidth = Math.max(
      constraints.minWidth,
      Math.min(constraints.maxWidth, clientX - currentRect.left)
    );
  }
  
  if (resizeDirection.includes('left')) {
    const deltaX = currentRect.left - clientX;
    newWidth = Math.max(
      constraints.minWidth,
      Math.min(constraints.maxWidth, currentSize.width + deltaX)
    );
    if (newWidth > constraints.minWidth) {
      newX = Math.max(0, clientX);
    }
  }
  
  if (resizeDirection.includes('bottom')) {
    newHeight = Math.max(
      constraints.minHeight,
      Math.min(constraints.maxHeight, clientY - currentRect.top)
    );
  }
  
  if (resizeDirection.includes('top')) {
    const deltaY = currentRect.top - clientY;
    newHeight = Math.max(
      constraints.minHeight,
      Math.min(constraints.maxHeight, currentSize.height + deltaY)
    );
    if (newHeight > constraints.minHeight) {
      newY = Math.max(0, clientY);
    }
  }

  return {
    size: { width: newWidth, height: newHeight },
    position: { x: newX, y: newY }
  };
};

/**
 * Gets dimensions for maximized window state (full viewport).
 * @returns Window state with full screen dimensions and (0,0) position
 */
export const getMaximizedDimensions = (): WindowState => {
  const fullWidth = Math.max(window.innerWidth, document.documentElement.clientWidth);
  const fullHeight = Math.max(window.innerHeight, document.documentElement.clientHeight);
  
  return {
    size: { width: fullWidth, height: fullHeight },
    position: { x: 0, y: 0 }
  };
};

/**
 * Custom React hook for managing draggable, resizable, and minimizable window state.
 * @param initialSize - Initial window dimensions
 * @param initialPosition - Initial window position
 * @param constraints - Size constraints for the window
 * @returns Object with state and control functions for window management
 */
export const useWindowManager = (
  initialSize: WindowSize = VIDEOCHAT_WINDOW_DEFAULTS.SIZE,
  initialPosition: WindowPosition = VIDEOCHAT_WINDOW_DEFAULTS.POSITION,
  constraints: WindowConstraints = VIDEOCHAT_WINDOW_DEFAULTS.CONSTRAINTS
) => {
  const [state, setState] = React.useState<WindowManagerState>(() =>
    createInitialWindowState(initialSize, initialPosition)
  );

  const updateState = React.useCallback((updates: Partial<WindowManagerState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const handleMouseDown = React.useCallback((e: React.MouseEvent, windowRef: React.RefObject<HTMLDivElement>) => {
    if (state.isMinimized) return;
    
    const target = e.target as HTMLElement;
    if (target.closest('[data-chat-area="true"]')) {
      return;
    }
    
    updateState({ isDragging: true });
    const rect = windowRef.current?.getBoundingClientRect();
    if (rect) {
      updateState({
        dragOffset: {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        }
      });
    }
  }, [state.isMinimized, updateState]);

  const handleMinimizedMouseDown = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    updateState({ isDraggingMinimized: true });
    const rect = e.currentTarget.getBoundingClientRect();
    updateState({
      minimizedDragOffset: {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      }
    });
  }, [updateState]);

  const handleResizeMouseDown = React.useCallback((e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    updateState({
      isResizing: true,
      resizeDirection: direction
    });
  }, [updateState]);

  const toggleMaximize = React.useCallback(() => {
    if (state.isMinimized) {
      updateState({ isMinimized: false });
      return;
    }

    if (state.isMaximized) {
      updateState({
        windowSize: state.previousWindowState.size,
        position: state.previousWindowState.position,
        isMaximized: false
      });
    } else {
      const maximizedState = getMaximizedDimensions();
      updateState({
        previousWindowState: {
          size: state.windowSize,
          position: state.position
        },
        windowSize: maximizedState.size,
        position: maximizedState.position,
        isMaximized: true
      });
    }
  }, [state.isMinimized, state.isMaximized, state.previousWindowState, state.windowSize, state.position, updateState]);

  const minimize = React.useCallback(() => {
    updateState({ isMinimized: true });
  }, [updateState]);

  const restore = React.useCallback(() => {
    if (state.isMaximized) {
      updateState({
        windowSize: state.previousWindowState.size,
        position: state.previousWindowState.position,
        isMaximized: false
      });
    } else if (state.isMinimized) {
      updateState({ isMinimized: false });
    }
  }, [state.isMaximized, state.isMinimized, state.previousWindowState, updateState]);

  const maximize = React.useCallback(() => {
    if (!state.isMaximized) {
      const maximizedState = getMaximizedDimensions();
      updateState({
        previousWindowState: {
          size: state.windowSize,
          position: state.position
        },
        windowSize: maximizedState.size,
        position: maximizedState.position,
        isMaximized: true
      });
    }
  }, [state.isMaximized, state.windowSize, state.position, updateState]);

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (state.isDragging && !state.isMinimized) {
        const newPosition = calculateDragPosition(
          e.clientX,
          e.clientY,
          state.dragOffset,
          state.windowSize,
          false
        );
        updateState({ position: newPosition });
      }

      if (state.isDraggingMinimized && state.isMinimized) {
        const newPosition = calculateDragPosition(
          e.clientX,
          e.clientY,
          state.minimizedDragOffset,
          { width: 300, height: 200 },
          true
        );
        updateState({ minimizedPosition: newPosition });
      }

      if (state.isResizing && !state.isMinimized) {
        const windowElement = document.querySelector('[data-window-ref="true"]') as HTMLElement;
        const rect = windowElement?.getBoundingClientRect();
        if (!rect) return;

        const { size, position } = calculateResizedDimensions(
          e.clientX,
          e.clientY,
          rect,
          state.windowSize,
          state.position,
          state.resizeDirection,
          constraints
        );

        updateState({
          windowSize: size,
          position: position
        });
      }
    };

    const handleMouseUp = () => {
      updateState({
        isDragging: false,
        isDraggingMinimized: false,
        isResizing: false,
        resizeDirection: ''
      });
    };

    if (state.isDragging || state.isResizing || state.isDraggingMinimized) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    state.isDragging,
    state.isResizing,
    state.isDraggingMinimized,
    state.dragOffset,
    state.minimizedDragOffset,
    state.position,
    state.minimizedPosition,
    state.windowSize,
    state.resizeDirection,
    state.isMinimized,
    constraints,
    updateState
  ]);

  return {
    state,
    updateState,
    handleMouseDown,
    handleMinimizedMouseDown,
    handleResizeMouseDown,
    toggleMaximize,
    minimize,
    restore,
    maximize
  };
};

/**
 * Creates a wrapped mouse down handler that filters out interactive elements.
 * Prevents dragging when clicking on buttons, inputs, or chat areas.
 * @param windowState - Current window manager state
 * @param handleMouseDown - Base mouse down handler function
 * @param windowRef - Reference to the window DOM element
 * @returns Wrapped mouse down handler with element filtering
 */
export const createHandleMouseDownWrapper = (
  windowState: WindowManagerState,
  handleMouseDown: (e: React.MouseEvent, windowRef: React.RefObject<HTMLDivElement>) => void,
  windowRef: React.RefObject<HTMLDivElement>
) => {
  return (e: React.MouseEvent) => {
    if (windowState.isMinimized) return;
    const target = e.target as HTMLElement;
    
    if (target.closest('[data-chat-area="true"]') ||
        target.closest('button') ||
        target.closest('[role="button"]') ||
        target.closest('.window-controls') ||
        target.closest('[data-window-control="true"]') ||
        target.closest('input') ||
        target.closest('select') ||
        target.closest('textarea') ||
        target.closest('[contenteditable="true"]') ||
        target.closest('.MuiButton-root') ||
        target.closest('.MuiIconButton-root') ||
        target.closest('[data-testid*="button"]') ||
        target.closest('[class*="button"]') ||
        target.closest('[class*="Button"]') ||
        target.tagName.toLowerCase() === 'button' ||
        target.tagName.toLowerCase() === 'input' ||
        target.tagName.toLowerCase() === 'select' ||
        target.tagName.toLowerCase() === 'textarea') {
      return;
    }
    
    handleMouseDown(e, windowRef);
  };
};

/**
 * Creates a wrapped resize mouse down handler with event propagation prevention.
 * @param handleResizeMouseDown - Base resize mouse down handler function
 * @returns Wrapped resize handler that prevents event propagation
 */
export const createHandleResizeMouseDownWrapper = (
  handleResizeMouseDown: (e: React.MouseEvent, direction: string) => void
) => {
  return (e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    handleResizeMouseDown(e, direction);
  };
};
